import React, { useEffect, useMemo, useState } from 'react';

import { Popover } from '@mui/material';
import produce from 'immer';
import { noop } from 'lodash';
import { useParams } from 'react-router-dom';

import useHashmapConverter from 'hooks/useHashmapConverter';
import { useQueryState } from 'hooks/useQueryState';

import { useGlobalFilterDataStore } from 'store/globalFilterData.store';

import { useQueryGetProjectDatesForResourceChart } from 'api/queries/getProjectDatesForResourceChart.query';

import { SideMenu } from 'components/common/CustomFilters/DateRangeFilter/SideMenu/SideMenu';
import { getInputLabel } from 'components/common/CustomFilters/TableDateRangeFilter/TableDateRangeFilter.utils';
import classes from 'components/common/GlobalFilterComponent/GlobalFilterComponent.module.scss';
import { Range } from 'components/common/GlobalFilterComponent/components/RangeFilter/rangeFilter';
import componentClasses from 'components/common/GlobalFilterComponent/components/dateRangeFilter/dateRangeFilter.module.scss';
import { DateRangeFilterMain } from 'components/common/GlobalFilterComponent/components/dateRangeFilter/dateRangeFilterMain';

import { ProgramStatusUrlParams } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/ProgramStatusSummaryReport/programStatusSummaryReportTypes';
import { TextInput } from 'components/DesignSystem/TextInput/TextInput';

export type RangeWithCustom = Range | 'R_CUSTOM';

export const rangeOptions: ISelectOption<RangeWithCustom>[] = [
    { label: '2 Weeks', value: 'R_2W' },
    { label: '4 Weeks', value: 'R_4W' },
    { label: '3 Months', value: 'R_3M' },
    { label: '6 Months', value: 'R_6M' },
    { label: '12 Months', value: 'R_12M' },
    { label: 'Custom', value: 'R_CUSTOM' },
];

interface Props {
    methods?: any;
    name: string;
    setState?: React.Dispatch<React.SetStateAction<any>>;
    handleFilters?: () => void;
    overrideLatestVersionId?: number | undefined;
    overrideCompareVersionId?: number | undefined;
    skipFormSet?: boolean;
}

export const DateRangeFilter = ({
    methods,
    name,
    setState,
    handleFilters,
    overrideLatestVersionId,
    overrideCompareVersionId,
    skipFormSet = false,
}: Props) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [menuOpen, setMenuOpen] = useState<boolean>(false);
    const [activeSideMenuOption, setActiveSideMenuOption] = useState<RangeWithCustom>('R_2W');

    //get version hashes from url params
    const { id, versionToCompareId } = useParams<ProgramStatusUrlParams>();

    // convert version hashes to string with comma between
    const hashcode = [id, versionToCompareId].filter((v): v is string => Boolean(v)).join(',');
    const { versionList: versionFromHashMap } = useHashmapConverter(hashcode);

    const latestVersionId = overrideLatestVersionId ?? versionFromHashMap[0]?.versionDbId;
    const compareVersionId = overrideCompareVersionId ?? versionFromHashMap[1]?.versionDbId;
    const { data } = useQueryGetProjectDatesForResourceChart({
        lastMetaDataId: latestVersionId,
        comparedMetaDataId: compareVersionId,
    });

    const [queryStringValue] = useQueryState(name);
    const { getValueFromStore } = useGlobalFilterDataStore();
    const storeValue = getValueFromStore<unknown>(name);
    const formValue = methods?.getValues(name);

    const isValue = useMemo(() => {
        return queryStringValue || storeValue || formValue;
    }, [queryStringValue, storeValue]);

    useEffect(() => {
        if (setState) {
            if (!isValue || isValue.length === 0) {
                setActiveSideMenuOption('R_2W');
                setState((prev) => {
                    return produce(prev, (draft) => {
                        draft[name].isReady = true;
                        draft[name].isEmpty = true;
                        return draft;
                    });
                });
            }

            if (isValue && isValue.length > 0 && !skipFormSet) {
                if (Array.isArray(isValue)) {
                    methods?.setValue(name, isValue.map(Number));
                    setActiveSideMenuOption('R_CUSTOM');
                } else {
                    methods?.setValue(name, isValue);
                    setActiveSideMenuOption(isValue);
                }

                setState((prev) => {
                    return produce(prev, (draft) => {
                        draft[name].isReady = true;
                        return draft;
                    });
                });
            }
        }
    }, [isValue, setState, methods, name]);

    const inputLabel = useMemo(() => {
        if (typeof formValue === 'string') {
            return rangeOptions.find((item) => item.value === formValue)?.label || '';
        }
        if (Array.isArray(formValue)) {
            return getInputLabel({ startDate: Number(formValue[0]), endDate: Number(formValue[1]) });
        }
        return '';
    }, [formValue]);

    const handleClear = () => {
        onApply('R_4W');
    };

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
        setMenuOpen(true);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
        setMenuOpen(false);
    };

    const onApply = (value: (number | null)[] | string) => {
        methods?.setValue(name, value);
        handleFilters && handleFilters();
        setMenuOpen(false);
    };

    return (
        <div className={`${classes.selectWrapper} ${componentClasses.inputWidth}`}>
            <TextInput
                id={name}
                placeholder={'Select date range...'}
                value={inputLabel}
                onClear={handleClear}
                onClick={handleMenuOpen}
                autoComplete={'off'}
                onChange={noop}
                label={'Date Range'}
            />
            <Popover
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                disableScrollLock
                anchorEl={anchorEl}
                onClose={handlePopoverClose}
                open={menuOpen}
                sx={{
                    '.MuiPopover-paper': {
                        overflow: 'visible',
                    },
                    '.MuiPopover-root': {
                        overflow: 'visible',
                    },
                }}
            >
                <div data-testid={'popover-content'} className={componentClasses.popoverContentWrapper}>
                    <div className={componentClasses.popoverContent}>
                        <SideMenu<RangeWithCustom>
                            menuOptions={rangeOptions}
                            activeSideMenuOption={activeSideMenuOption}
                            setActiveSideMenuOption={setActiveSideMenuOption}
                            className={componentClasses.sideMenu}
                        />
                        <DateRangeFilterMain
                            activeSideMenuOption={activeSideMenuOption}
                            value={formValue}
                            onChange={onApply}
                            handlePopoverClose={handlePopoverClose}
                            minDate={data?.startProjectDate}
                            maxDate={data?.endProjectDate}
                        />
                    </div>
                </div>
            </Popover>
        </div>
    );
};
